import React from "react";
import { useFela } from "react-fela";
import Trans from "next-translate/Trans";
import useTranslation from "next-translate/useTranslation";

import { HalfWrapper } from "@/components/HalfWrapper";
import { Heading } from "@/components/Heading";
import { useTheme } from "@/hooks/useTheme";
import { StyledIconWrapper, Icon } from "@/components/Icon";
import { IIconType } from "@/icons";

import { FileRepair } from "@/containers/FileRepair";

type IProps = JSX.IntrinsicElements["div"];

export const BrokenXDSection: React.FC<IProps> = () => {
  const { css } = useFela();
  const { bp, colors } = useTheme();
  const { t } = useTranslation("section/brokenXD");

  const bullets: Array<{ iconType: IIconType; text: string }> = [
    { iconType: "OUTDATED-ICON", text: "outdated" },
    { iconType: "CORRUPTED-ICON", text: "corrupted" },
  ];

  return (
    <section
      id="BrokenXDSection"
      className={css({
        height: "100%",
        minHeight: 750,
        display: "flex",
        justifyContent: "flex-end",
        flexDirection: "column",
        [bp.FROM_DESKTOP]: {
          flexDirection: "row",
        },
      })}
    >
      <div
        className={css({
          flex: "1 1 0%",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-start",
          backgroundImage: `url('/svgs/colorful-bg.svg'), linear-gradient(44deg, ${colors.GRADIENT_BLUE_DARK} 40%, ${colors.GRADIENT_PURPLE} 105%)`,
          backgroundSize: "cover",
        })}
      >
        <div
          className={css({
            minHeight: 561,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            padding: "0 20px",
            color: "white",
            [bp.FROM_TABLET]: {
              minHeight: 410,
              width: "100%",
            },
            [bp.FROM_DESKTOP]: {
              maxWidth: 670,
              alignItems: "flex-start",
            },
          })}
        >
          <Heading
            customStyle={{
              marginTop: 200,
              [bp.FROM_TABLET]: { marginTop: 100 },
              [bp.FROM_DESKTOP]: {
                fontSize: 48,
                textAlign: "left",
                marginTop: 160,
              },
            }}
          >
            <Trans
              i18nKey="section/brokenXD:title"
              components={[
                <span
                  key={0}
                  className={css({
                    whiteSpace: "nowrap",
                  })}
                />,
                <StyledIconWrapper
                  key={1}
                  wrapperStyle={{
                    display: "inline-block",
                    alignItems: "center",
                    height: 1.38,
                  }}
                  customStyle={{
                    height: 47,
                    width: 48,
                    verticalAlign: "middle",
                  }}
                >
                  <Icon iconType="ADOBEXD" />
                </StyledIconWrapper>,
              ]}
            />
          </Heading>

          <ul
            className={css({
              paddingLeft: 35,
              listStyle: "none",
              [bp.FROM_TABLET]: { marginTop: 60, paddingLeft: 25 },
              [bp.FROM_DESKTOP]: {
                marginTop: 20,
                marginLeft: 100,
              },
            })}
          >
            {bullets.map(b => (
              <li
                key={b.iconType}
                className={css({
                  fontSize: 18,
                  marginBottom: 20,
                  paddingLeft: 10,
                  position: "relative",
                })}
              >
                <StyledIconWrapper
                  wrapperStyle={{
                    display: "inline-block",
                    alignItems: "center",
                    height: 1.38,
                    position: "absolute",
                    left: -25,
                    top: -2,
                  }}
                  customStyle={{
                    height: 28,
                    width: 28,
                    verticalAlign: "middle",
                  }}
                >
                  <Icon iconType={b.iconType} />
                </StyledIconWrapper>

                {t(`bullets.${b.text}`)}
              </li>
            ))}
          </ul>

          <p
            className={css({
              fontSize: 18,
              [bp.FROM_TABLET]: {
                marginTop: 40,
              },
              [bp.FROM_DESKTOP]: {
                marginTop: 10,
                marginLeft: 80,
              },
            })}
          >
            <Trans
              i18nKey="section/brokenXD:description"
              components={[
                <em
                  key={0}
                  className={css({
                    fontStyle: "normal",
                    fontSize: 30,
                    fontWeight: 700,
                    verticalAlign: "middle",
                  })}
                />,
              ]}
            />
          </p>
        </div>
      </div>

      <div
        className={css({
          flex: "1 1 0%",
          display: "flex",
          background: colors.CULTURED,
          justifyContent: "center",
          padding: "40px 20px",
          [bp.FROM_DESKTOP]: {
            padding: 0,
            justifyContent: "flex-start",
          },
        })}
      >
        <HalfWrapper>
          <FileRepair />
        </HalfWrapper>
      </div>
    </section>
  );
};
